// Rule-based emoji inference for activity titles. Pure function — no DB,
// no AI, no cost. Runs on every render. Order matters: more specific
// patterns first. All patterns are case-insensitive.

const RULES: { pattern: RegExp; emoji: string }[] = [
  // Travel
  { pattern: /flight|vuelo/i, emoji: '✈️' },
  { pattern: /\bhotel\b|check.?in|check.?out/i, emoji: '🏨' },
  { pattern: /travel\s*day|\bviaje\b|\btraslado\b/i, emoji: '🧳' },
  { pattern: /\bbus\b|\bvan\b|shuttle|\bdrive\b|driving/i, emoji: '🚐' },
  { pattern: /\btrain\b|\btren\b/i, emoji: '🚆' },

  // Show-related
  { pattern: /soundcheck|sound\s*check|prueba\s*de\s*sonido/i, emoji: '🎸' },
  { pattern: /\brehearsal\b|\bensayo\b/i, emoji: '🎼' },
  { pattern: /load.?in|load.?out|\bcarga\b|\bdescarga\b/i, emoji: '📦' },
  { pattern: /\bshow\b|concert|concierto|\bgig\b|presentaci[oó]n/i, emoji: '🎤' },
  { pattern: /\bvenue\b|arena|estadio|stadium/i, emoji: '🏟️' },

  // Meals (specific → generic)
  { pattern: /breakfast|desayuno/i, emoji: '🥐' },
  { pattern: /lunch|almuerzo/i, emoji: '🍽️' },
  { pattern: /dinner|cena/i, emoji: '🍝' },
  { pattern: /\bmeal\b|comida|catering/i, emoji: '🍴' },

  // Media / press
  { pattern: /meet\s*(&|and)\s*greet|\bm&g\b|signing|aut[oó]grafos?/i, emoji: '🤝' },
  { pattern: /interview|entrevista/i, emoji: '🎙️' },
  { pattern: /\bradio\b/i, emoji: '📻' },
  { pattern: /\btv\b|televisi[oó]n/i, emoji: '📺' },
  { pattern: /press|prensa|photo|\bfoto\b|photoshoot/i, emoji: '📸' },

  // Party / rest (afterparty before party)
  { pattern: /afterparty|after.?party|after.?show/i, emoji: '🥂' },
  { pattern: /party|fiesta|celebraci[oó]n/i, emoji: '🥂' },
  { pattern: /day\s*off|off\s*day|\brest\b|descanso|d[ií]a\s*libre|free\s*day/i, emoji: '💤' },

  // Logistics
  { pattern: /lobby\s*call|\blobby\b|call\s*time/i, emoji: '🧳' },
  { pattern: /meeting|reuni[oó]n|\bsync\b|briefing/i, emoji: '🗣️' },
  { pattern: /studio|estudio/i, emoji: '🎚️' },
  { pattern: /backstage|camerino|camar[ií]n/i, emoji: '🎭' },

  // Other
  { pattern: /workout|\bgym\b|gimnasio|entrenamiento/i, emoji: '💪' },
  { pattern: /massage|masaje/i, emoji: '💆' },
  { pattern: /\bvideo\b|recording|grabaci[oó]n|\bshoot\b/i, emoji: '🎥' },
  { pattern: /\bmerch\b|merchandise/i, emoji: '🛍️' },
]

// Skip inference when the user already put a symbol/emoji at the start —
// avoids double icons like "🎸 🎸 Soundcheck".
function startsWithSymbol(title: string): boolean {
  const trimmed = title.trim()
  if (!trimmed) return false
  return !/[\p{L}\p{N}]/u.test(trimmed.charAt(0))
}

export function getActivityEmoji(title: string): string | null {
  if (!title || startsWithSymbol(title)) return null
  for (const rule of RULES) {
    if (rule.pattern.test(title)) return rule.emoji
  }
  return null
}

// Convenience: returns "emoji title" or just "title" if no match.
export function titleWithEmoji(title: string): string {
  const emoji = getActivityEmoji(title)
  return emoji ? `${emoji} ${title}` : title
}
